import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Navigation, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { every as _every, flatMap as _flatMap, some as _some } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { AutoPaySelectors } from '@amfam/billing/auto-pay/data-access';
import { AutoPayUtilSelectors } from '@amfam/billing/auto-pay/util';
import {
  BillAccountsSelectors,
  BillingNotificationsState
} from '@amfam/billing/billaccount/data-access';
import { PendingPayment, pendingPaymentQuery } from '@amfam/billing/pending-payment/data-access';
import {
  BillingRegistrationSelectors,
  PendingRegistration
} from '@amfam/billing/registration/data-access';
import { PolicySelectors } from '@amfam/policy/data-access';
// Store
import { AnalyticsFacade } from '@amfam/shared/analytics';
import {
  AutomaticPayment,
  BillAccount,
  BillingAnalytics,
  BillingElementIds,
  BillingPaymentPaths,
  INVALID_IMPERSONATION_STATUS_CODE
} from '@amfam/shared/models';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService, ScrollService } from '@amfam/shared/utility/shared-services';
import {
  Action,
  DockingBarService,
  ErrorLevels,
  ErrorObj,
  LoadingSpinnerService,
  PageTitleService
} from '@amfam/ui-kit';

import * as fromRoot from '../../core/store';

@Component({
  selector: 'app-bill-account-section',
  templateUrl: './bill-account-section.component.html',
  styleUrls: ['./bill-account-section.component.scss'],
  standalone: false
})
export class BillAccountSectionComponent implements OnInit, OnDestroy {
  billAccountsData: BillAccount[];
  userRegiseredBillAccountsData: BillAccount[];
  billAccountLoadError$: Observable<boolean>;
  pendingPayments$: Observable<PendingPayment[]>;
  pendingRegistrations$: Observable<PendingRegistration[]>;
  hasAdvancedPolicies$: Observable<boolean>;
  hasPastDue$: Observable<boolean>;
  pastDueEnabled: boolean;
  public billacountsEligibleForAutoPay$: Observable<string[]>;
  public billacountsHasAutoPay$: Observable<AutomaticPayment[]>;
  public billacountsELigibleAutoPay$: Observable<BillAccount[]>;
  showPaymentsOrMethodsAccordion = true;
  paymentHistoryElementIdConst = BillingElementIds.PAYMENT_HISTORY_ACCORDION;
  silentRegistrationError = false;
  errorList: Array<ErrorObj> = [];
  accordionStatus = {
    methodsAccordion: false,
    paymentsAccordion: false,
    paymentHistoryAccordion: false,
    billingHistoryAccordion: false
  };
  newBillingDesign: boolean;
  oldBillingDesign: boolean;
  navigation: Navigation;

  // **** Start of Analytics data for this component
  autoPayAccordionAnalytic = BillingAnalytics.clickAutoPayAccordion;
  scheduledPaymentAccordionAnalytic = BillingAnalytics.clickScheduledPaymentsAccordion;
  expandAllClickAnalytic = BillingAnalytics.clickAccordionExpandAll;
  payBillsAccordionAnalytic = BillingAnalytics.payBillsAccordionAnalytic;
  billingDashboardEnabled = this.feature.isEnabled('billing_dashboard');
  isContactBillingToggleEnabled = this.feature.isEnabled('contact_billing');

  manageAutoPay = this.copyService.getCopy('billing.billingDetailSections.manageAutoPay');
  paymentMethods = this.copyService.getCopy('billing.billingDetailSections.paymentMethods');
  myPaymentMethods = this.copyService.getCopy('billing.billingDetailSections.myPaymentMethods');
  preferences = this.copyService.getCopy('billing.billingDetailSections.preferences');
  paymentHistory = this.copyService.getCopy('billing.billingDetailSections.paymentHistory');
  statementHistory = this.copyService.getCopy('billing.billingDetailSections.statementHistory');
  stopManagingAccount = this.copyService.getCopy(
    'billing.billingDetailSections.stopManagingAccount'
  );
  futurePaymentsSchedule = this.copyService.getCopy(
    'billing.billingDetailSections.futurePaymentsSchedule'
  );
  manageOneTimePayments = this.copyService.getCopy(
    'billing.billingDetailSections.manageOneTimePayments'
  );
  payBills = this.copyService.getCopy('billing.billingDetailSections.payBills');

  selectedSection = 'payment';
  billingTitlesCopy = {};
  private billingSectionPageAnalytic = BillingAnalytics.pageBillingSection;
  // **** End of Analytics data for this component
  private cusCareNumber: string;
  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store,
    private dockingService: DockingBarService,
    private pageTitle: PageTitleService,
    public spinner: LoadingSpinnerService,
    private analyticsFacade: AnalyticsFacade,
    private copyService: CopyService,
    private feature: FeatureFlagService,
    private route: ActivatedRoute,
    private router: Router,
    private scrollService: ScrollService,
    private breakpointObserver: BreakpointObserver
  ) {
    this.navigation = this.router.getCurrentNavigation();
  }

  ngOnInit() {
    // This page uses localized spinners. Call stop in case they came here from somewhere that didn't stop the spinner.
    this.spinner.stop();
    if (this.newBillingDesign) {
      this.dockingService.hide();
    } else {
      this.dockingService.registerHeading('Billing & Payments');
      this.pageTitle.set('Billing & Payments');
      if (this.isContactBillingToggleEnabled) {
        this.dockingService.registerAction(
          new Action('Contact Billing', () => {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [BillingPaymentPaths.BILLING_CONTACT]
              })
            );
          })
        );
      }
    }

    // Determine if there was an error loading bill accounts
    this.billAccountLoadError$ = this.store.select(
      BillAccountsSelectors.selectBillAccountsHasError
    );

    // Pending payments are used to track which bill accounts are selected for payment
    this.pendingPayments$ = this.store.select(pendingPaymentQuery.selectPendingPayments);

    // Pending registrations determine if a silent registration is required
    this.pendingRegistrations$ = this.store.select(
      BillingRegistrationSelectors.selectPendingRegistrations
    );

    // Determine if the user has any advanced policies
    this.hasAdvancedPolicies$ = this.store.select(PolicySelectors.selectHasAdvancePolicies);

    // Determine if the user has any classic past due bill accounts
    this.hasPastDue$ = this.store.select(BillAccountsSelectors.selectHasPastDue);
    this.pastDueEnabled = this.feature.isEnabled('classic_past_due');

    // Flags to determine new vs old billing design
    this.newBillingDesign = this.feature.isEnabled('new-billing-design');
    this.oldBillingDesign = this.feature.isEnabled('old-billing-design');

    combineLatest(
      this.store.select(BillAccountsSelectors.selectBillAccounts),
      this.store.select(BillAccountsSelectors.selectUserRegisteredBillAccounts),
      this.store.select(AutoPaySelectors.getAutomaticPayments),
      this.store.select(fromRoot.getBillaccountsListEligibleForAutopay),
      this.store.select(AutoPayUtilSelectors.selectBillAccountsEligibleForAutoPay),
      (
        billAccounts,
        userRegisteredBillAccounts,
        automaticPayments,
        _oldBillAccountsEligibleForAutoPay,
        billAccountsEligibleForAutoPay
      ) => ({
        billAccounts: billAccounts,
        userRegisteredBillAccounts: userRegisteredBillAccounts,
        automaticPayments: automaticPayments,
        eligibleBillAccts: billAccountsEligibleForAutoPay
      })
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        this.billAccountsData = state.billAccounts;
        this.userRegiseredBillAccountsData = state.userRegisteredBillAccounts;

        // Hide the payment methods & payment accordions if all bill accounts are read-only
        this.showPaymentsOrMethodsAccordion = !_every(this.billAccountsData, ['readOnly', true]);
      });

    /**
     * AS: Scroll to top. Firefox doesnt always scroll to top when redirecting.
     *  Bug: Bug 213190
     */
    window.scrollTo(0, 0);

    this.store
      .select(BrandSelectors.selectCusCareNumber)
      .pipe(take(1))
      .subscribe(number => (this.cusCareNumber = number));

    this.billacountsEligibleForAutoPay$ = this.store.pipe(
      select(pendingPaymentQuery.selectPendingPayments),
      withLatestFrom(
        this.store.select(fromRoot.getBillaccountsListEligibleForAutopay),
        this.store.select(AutoPayUtilSelectors.selectBillAccountsEligibleForAutoPay)
      ),
      map(([pendingPayments, , billAccountsEligibleForAutopay]) => {
        const eligibleAccounts: BillAccount[] = billAccountsEligibleForAutopay;
        return _flatMap(pendingPayments, payment => payment.billAccountNumber).filter(ba => {
          if (eligibleAccounts && eligibleAccounts.length === 0) {
            return false;
          } else {
            return eligibleAccounts && eligibleAccounts.length > 0
              ? eligibleAccounts.find(ap => ap.billAccountNumber === ba)
              : true;
          }
        });
      })
    );

    this.billacountsELigibleAutoPay$ = this.store.select(
      AutoPayUtilSelectors.selectBillAccountsEligibleForAutoPay
    );

    this.billacountsHasAutoPay$ = this.store.select(AutoPaySelectors.getAutomaticPayments);

    // Send to Analytics
    this.analyticsFacade.trackPage(this.billingSectionPageAnalytic);

    // open payment methods accordion
    this.route.queryParams.pipe(take(1)).subscribe(queryParams => {
      if (queryParams.openAccordion === 'paymentMethods') {
        this.accordionStatus.methodsAccordion = true;
      }
    });

    this.billingTitlesCopy = this.copyService.getCopy('billing.newBillingDesignTitles');
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  toggleAccordion(accordionName: string, event: any) {
    this.accordionStatus[accordionName] = event.isOpen;
  }

  expandAllClicked() {
    this.analyticsFacade.trackButtonClick(this.expandAllClickAnalytic);
  }

  goToSection() {
    const goTo = this.route.snapshot.queryParamMap.get('goTo');
    if (goTo) {
      setTimeout(() => {
        if (goTo === 'paymentHistory' || goTo === 'statementHistory') {
          const isSmallScreen = this.breakpointObserver.isMatched(
            '(max-width: 480px) and (orientation: portrait)'
          );
          if (isSmallScreen) {
            this.scrollService.scrollToElement('paymentHistory', 85);
          } else {
            this.scrollService.scrollToElement('paymentHistory', 205);
          }
          if (goTo === 'statementHistory') {
            this.selectedSection = 'statement';
          } else {
            this.selectedSection = 'payment';
          }
        }
      });
    }
  }

  silentRegistrationFailure(billAccountsNotifications: BillingNotificationsState[]) {
    this.silentRegistrationError = true;
    const isImpersonationError = _some(billAccountsNotifications, {
      updateRegistrationError: INVALID_IMPERSONATION_STATUS_CODE
    });
    const message = isImpersonationError
      ? this.copyService.getCopy('billing.errorMsgsModified[0].202304')
      : this.copyService.getCopy('billing.cannotSchedule') + this.cusCareNumber + '.';
    this.errorList = this.showErrorMessage([message]);
  }

  selectSection(section: string): void {
    this.selectedSection = section;
  }

  private showErrorMessage(errorMessageList: string[], errorLevel?: ErrorLevels): Array<ErrorObj> {
    let errorList: Array<ErrorObj> = [];
    errorMessageList.forEach((msg: string) => {
      if (!errorList.find(error => error.errorMessage === msg)) {
        errorList = [
          ...errorList,
          { errorMessage: msg, errorLevel: errorLevel || ErrorLevels.HIGH }
        ];
      }
    });
    return errorList;
  }

  redirectToPaymentMethod() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [BillingPaymentPaths.VIEW_PAYMENT_ACCOUNTS]
      })
    );
  }

  redirectToPayment() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [BillingPaymentPaths.ADD_ONE_TIME_PAYMENT]
      })
    );
  }

  redirectToManageAutopay() {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [BillingPaymentPaths.MANAGE_AUTOPAY]
      })
    );
  }
}
